import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { CreditCard, LogOut, ChevronDown, CheckCircle } from 'lucide-react';
import LogoutModal from './LogoutModal';
import { BillingModal } from '../../Billing/components/BillingModal';
import ConfettiExplosion from 'react-confetti-explosion';

interface ProfileDropdownProps {
  email: string | null;
}

const ProfileDropdown: React.FC<ProfileDropdownProps> = ({ email }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const [showBillingModal, setShowBillingModal] = useState(false);
  const [showConfetti, setShowConfetti] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [upgradedPlan, setUpgradedPlan] = useState<string>('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get the first letter of the email for the avatar
  const initial = email ? email.charAt(0).toUpperCase() : 'U';

  // Generate a consistent color based on the email
  const getAvatarColor = (email: string | null) => {
    if (!email) return 'from-gray-400 to-gray-600';
    const colors = [
      'from-purple-400 to-purple-600',
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-yellow-400 to-yellow-600',
      'from-pink-400 to-pink-600',
      'from-indigo-400 to-indigo-600',
    ];
    const index = email.charCodeAt(0) % colors.length;
    return colors[index];
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSignOutClick = () => {
    setIsOpen(false); // Close dropdown
    setShowLogoutModal(true); // Show confirmation modal
  };

  const handleBilling = () => {
    setIsOpen(false); // Close dropdown
    setShowBillingModal(true); // Open billing modal
  };

  const handleUpgradeSuccess = (planName: string) => {
    // Close billing modal
    setShowBillingModal(false);
    
    // Show celebration after a brief delay
    setTimeout(() => {
      setUpgradedPlan(planName);
      setShowConfetti(true);
      setShowSuccessMessage(true);
      
      // Hide success message after a few seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 4000);
      
      // Reset confetti after animation
      setTimeout(() => {
        setShowConfetti(false);
      }, 3500);
    }, 300);
  };

  // Render celebration components using React Portal at document.body
  const celebrationContent = (
    <>
      {/* Confetti Celebration - Rendered at document root */}
      {showConfetti && (
        <div className="fixed inset-0 flex items-center justify-center z-[9999] pointer-events-none">
          <ConfettiExplosion
            force={0.8}
            duration={3000}
            particleCount={250}
            width={1600}
            colors={['#0EA5E9', '#38BDF8', '#7DD3FC', '#BAE6FD', '#E0F2FE']}
          />
        </div>
      )}

      {/* Success Message - Centered properly */}
      <AnimatePresence>
        {showSuccessMessage && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 30
            }}
            className="fixed inset-0 flex items-start justify-center pt-20 z-[9998] pointer-events-none"
          >
            <div className="bg-white rounded-2xl shadow-2xl border border-gray-100 px-8 py-6 flex items-center space-x-4 pointer-events-auto">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: "spring",
                  stiffness: 400,
                  damping: 20,
                  delay: 0.2
                }}
              >
                <CheckCircle className="w-8 h-8 text-green-500" strokeWidth={1.5} />
              </motion.div>
              <div>
                <h3 className="text-lg font-light text-gray-900">Welcome to {upgradedPlan}!</h3>
                <p className="text-sm text-gray-500 mt-0.5">Your account has been upgraded successfully</p>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Profile Button */}
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-2.5 px-3 py-1.5 bg-white/80 backdrop-blur-lg border border-gray-200/50 rounded-full shadow-sm hover:shadow-md transition-shadow"
        layout
      >
        {/* Avatar with gradient background */}
        <motion.div 
          className={`w-7 h-7 rounded-full bg-gradient-to-br ${getAvatarColor(email)} flex items-center justify-center flex-shrink-0`}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 400, damping: 20 }}
        >
          <span className="text-white text-xs font-semibold">{initial}</span>
        </motion.div>

        {/* Email with animated width */}
        <AnimatePresence>
          {email && (
            <motion.span 
              initial={{ width: 0, opacity: 0 }}
              animate={{ width: "auto", opacity: 1 }}
              exit={{ width: 0, opacity: 0 }}
              transition={{ 
                duration: 0.3,
                ease: "easeOut",
                opacity: { duration: 0.2, delay: 0.1 }
              }}
              className="text-sm text-gray-700 font-medium overflow-hidden whitespace-nowrap"
              style={{ maxWidth: '150px' }}
            >
              {email}
            </motion.span>
          )}
        </AnimatePresence>

        {/* Chevron */}
        <motion.div
          initial={{ scale: 0, rotate: 0 }}
          animate={{ scale: 1, rotate: isOpen ? 180 : 0 }}
          transition={{ 
            scale: { type: "spring", stiffness: 400, damping: 20, delay: 0.2 },
            rotate: { duration: 0.2, ease: "easeOut" }
          }}
        >
          <ChevronDown className="w-3.5 h-3.5 text-gray-400" strokeWidth={2} />
        </motion.div>
      </motion.button>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -10 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -10 }}
            transition={{
              type: "spring",
              stiffness: 400,
              damping: 25,
              duration: 0.2
            }}
            className="absolute right-0 mt-2 w-56 origin-top-right"
          >
            <div className="bg-white/95 backdrop-blur-xl rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden">
              {/* Email header */}
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center space-x-2.5">
                  <div className={`w-8 h-8 rounded-full bg-gradient-to-br ${getAvatarColor(email)} flex items-center justify-center`}>
                    <span className="text-white text-sm font-semibold">{initial}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-gray-500">Signed in as</p>
                    <p className="text-sm font-medium text-gray-900 truncate">{email}</p>
                  </div>
                </div>
              </div>

              {/* Menu Items */}
              <div className="py-1">
                {/* Billing */}
                <motion.button
                  whileHover={{ backgroundColor: 'rgba(249, 250, 251, 1)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleBilling}
                  className="w-full px-4 py-2.5 flex items-center space-x-3 text-gray-700 transition-colors"
                >
                  <CreditCard className="w-4 h-4 text-gray-500" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Billing</span>
                </motion.button>

                {/* Divider */}
                <div className="mx-3 my-1 border-t border-gray-100" />

                {/* Sign Out */}
                <motion.button
                  whileHover={{ backgroundColor: 'rgba(254, 242, 242, 1)' }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSignOutClick}
                  className="w-full px-4 py-2.5 flex items-center space-x-3 text-red-600 transition-colors"
                >
                  <LogOut className="w-4 h-4 text-red-500" strokeWidth={1.5} />
                  <span className="text-sm font-medium">Sign out</span>
                </motion.button>
              </div>

              {/* Bottom accent line */}
              <div className="h-0.5 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Logout Confirmation Modal */}
      <LogoutModal 
        isOpen={showLogoutModal} 
        onClose={() => setShowLogoutModal(false)} 
      />

      {/* Billing Modal */}
      <BillingModal
        isOpen={showBillingModal}
        onClose={() => setShowBillingModal(false)}
        onUpgradeSuccess={handleUpgradeSuccess}
      />

      {/* Render celebration at document root using portal */}
      {(showConfetti || showSuccessMessage) && ReactDOM.createPortal(
        celebrationContent,
        document.body
      )}
    </div>
  );
};

export default ProfileDropdown;